'use client';

import * as React from 'react';
import Link from 'next/link';
import { ArrowUpRight } from 'lucide-react';
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  type PanInfo
} from 'motion/react';

import { capabilities, capabilityHref } from '~/lib/capabilities';
import { ExampleShortlist } from './example-shortlist';
import { HomepageMonitoringPreview } from './homepage-monitoring-preview';
import { IntelligenceExplorerPreview } from './intelligence-explorer-previews';

const PREVIEW_OPTIONS = capabilities;

export function IntelligenceExplorerSection(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const reduced = !!shouldReduceMotion;
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [direction, setDirection] = React.useState(1);
  const selectorRef = React.useRef<HTMLDivElement>(null);
  const navigationRef = React.useRef<HTMLElement>(null);
  const previewScrollFrame = React.useRef(0);
  const wheelDelta = React.useRef(0);
  const wheelResetTimer = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const lastWheelNavigation = React.useRef(0);
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const stageRotateX = useSpring(pointerY, {
    stiffness: 130,
    damping: 24,
    mass: 0.8
  });
  const stageRotateY = useSpring(pointerX, {
    stiffness: 130,
    damping: 24,
    mass: 0.8
  });
  const activeOption = PREVIEW_OPTIONS[activeIndex];

  React.useEffect(() => {
    const selector = selectorRef.current;
    if (!selector) return;
    const keepSelectionVisible = () => {
      const selected = selector.querySelector<HTMLElement>(
        '[aria-current="true"]'
      );
      if (!selected) return;
      const item = selected.getBoundingClientRect();
      const bounds = selector.getBoundingClientRect();
      if (item.left < bounds.left || item.right > bounds.right) {
        selector.scrollTo({
          left:
            selector.scrollLeft +
            item.left -
            bounds.left -
            (bounds.width - item.width) / 2,
          behavior: reduced ? 'instant' : 'smooth'
        });
      }
    };
    keepSelectionVisible();
    const observer = new ResizeObserver(keepSelectionVisible);
    observer.observe(selector);
    return () => observer.disconnect();
  }, [activeIndex, reduced]);

  const revealPreview = React.useCallback(() => {
    cancelAnimationFrame(previewScrollFrame.current);
    // Measure after selection/focus has committed so keyboard and pointer
    // navigation use the same final position beneath the sticky navbar.
    previewScrollFrame.current = requestAnimationFrame(() => {
      const navigation = navigationRef.current;
      if (!navigation) return;
      // Only move down on an explicit choice, never on playback/wheel/swipe.
      const distance = navigation.getBoundingClientRect().top - 80;
      if (distance > 8) {
        window.scrollBy({
          top: distance,
          behavior: reduced ? 'instant' : 'smooth'
        });
      }
    });
  }, [reduced]);

  const selectPreview = React.useCallback(
    (nextIndex: number) => {
      if (nextIndex === activeIndex) return;
      setDirection(nextIndex > activeIndex ? 1 : -1);
      setActiveIndex(nextIndex);
    },
    [activeIndex]
  );

  const stepPreview = React.useCallback((step: -1 | 1) => {
    setDirection(step);
    setActiveIndex(
      (current) =>
        (current + step + PREVIEW_OPTIONS.length) % PREVIEW_OPTIONS.length
    );
  }, []);

  const handleSelectorKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    let nextIndex: number;
    switch (event.key) {
      case 'ArrowLeft':
        nextIndex =
          (index - 1 + PREVIEW_OPTIONS.length) % PREVIEW_OPTIONS.length;
        break;
      case 'ArrowRight':
        nextIndex = (index + 1) % PREVIEW_OPTIONS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = PREVIEW_OPTIONS.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      setDirection(event.key === 'ArrowLeft' ? -1 : 1);
      setActiveIndex(nextIndex);
    } else {
      selectPreview(nextIndex);
    }
    selectorRef.current
      ?.querySelector<HTMLButtonElement>(
        `[data-capability-index="${nextIndex}"]`
      )
      ?.focus({ preventScroll: true });
    revealPreview();
  };

  React.useEffect(
    () => () => {
      if (wheelResetTimer.current) clearTimeout(wheelResetTimer.current);
      cancelAnimationFrame(previewScrollFrame.current);
    },
    []
  );

  const resetStageTilt = React.useCallback(() => {
    pointerX.set(0);
    pointerY.set(0);
  }, [pointerX, pointerY]);

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (reduced || event.pointerType !== 'mouse') return;
      const bounds = event.currentTarget.getBoundingClientRect();
      const normalizedX =
        ((event.clientX - bounds.left) / Math.max(bounds.width, 1)) * 2 - 1;
      const normalizedY =
        ((event.clientY - bounds.top) / Math.max(bounds.height, 1)) * 2 - 1;

      // Complete Shelf previews the focused object at roughly two degrees.
      pointerX.set(normalizedX * -1.8);
      pointerY.set(normalizedY * 1.25);
    },
    [pointerX, pointerY, reduced]
  );

  const handleWheel = React.useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      // The source takes over the whole viewport. Here we reserve vertical
      // wheel movement for the landing page and use horizontal trackpad intent.
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      event.preventDefault();
      wheelDelta.current += event.deltaX;

      if (wheelResetTimer.current) clearTimeout(wheelResetTimer.current);
      wheelResetTimer.current = setTimeout(() => {
        wheelDelta.current = 0;
      }, 140);

      const now = performance.now();
      if (
        Math.abs(wheelDelta.current) < 44 ||
        now - lastWheelNavigation.current < 360
      ) {
        return;
      }

      stepPreview(wheelDelta.current > 0 ? 1 : -1);
      lastWheelNavigation.current = now;
      wheelDelta.current = 0;
    },
    [stepPreview]
  );

  const handleDragEnd = React.useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (Math.abs(info.offset.x) < 52 && Math.abs(info.velocity.x) < 420) {
        return;
      }

      stepPreview(info.offset.x < 0 || info.velocity.x < -420 ? 1 : -1);
    },
    [stepPreview]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || event.target !== event.currentTarget)
        return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        stepPreview(event.key === 'ArrowLeft' ? -1 : 1);
      }
    },
    [stepPreview]
  );

  return (
    <section
      id="intelligence-explorer"
      aria-label="Lumnis capability previews"
      className="relative scroll-mt-20 overflow-hidden bg-[color:var(--paper)] pt-3 pb-10 md:pt-4 md:pb-14 [overflow-anchor:none]"
    >
      <div className="mx-auto w-full max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <nav
          ref={navigationRef}
          aria-label="Capability preview selector"
          className="mb-4 min-w-0 md:mb-5"
        >
          <div className="mb-1 flex min-h-11 items-center">
            <p className="flex items-center gap-3 text-[13px] font-medium text-[color:var(--ink-3)]">
              Explore capabilities
              <span className="font-mono text-[11px] tabular-nums text-[color:var(--ink-4)] lg:hidden">
                {activeIndex + 1} / {PREVIEW_OPTIONS.length}
              </span>
            </p>
          </div>
          <div
            ref={selectorRef}
            className="overflow-x-auto overscroll-x-contain border-b border-[color:var(--hairline)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <ul className="flex min-w-max gap-1 lg:grid lg:min-w-0 lg:grid-cols-6">
              {PREVIEW_OPTIONS.map((option, index) => {
                const selected = index === activeIndex;
                return (
                  <li
                    key={option.number}
                    className="min-w-0"
                  >
                    <button
                      type="button"
                      data-capability-index={index}
                      onClick={() => {
                        selectPreview(index);
                        revealPreview();
                      }}
                      onKeyDown={(event) => handleSelectorKeyDown(event, index)}
                      aria-current={selected ? 'true' : undefined}
                      aria-controls="product-preview-stage"
                      className={`relative flex min-h-14 w-[164px] items-center justify-center rounded-t-lg px-3 py-2 text-center text-[12px] leading-snug transition-colors duration-200 hover:bg-[color:var(--paper-2)] focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500 sm:text-[13px] lg:w-full ${selected ? 'bg-[color:var(--mint)] font-semibold text-[color:var(--accent-darker)]' : 'font-medium text-[color:var(--ink-3)]'}`}
                    >
                      {option.title}
                      {selected && (
                        <motion.span
                          aria-hidden
                          layoutId="capability-preview-position"
                          className="absolute inset-x-3 bottom-0 h-0.5 bg-[color:var(--accent-deep)]"
                          transition={
                            reduced
                              ? { duration: 0 }
                              : { type: 'spring', stiffness: 360, damping: 34 }
                          }
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
        <div className="overflow-hidden rounded-[24px] border border-[color:var(--hairline)] bg-[color:var(--preview-surface)] shadow-[0_22px_70px_rgba(32,26,20,0.09)]">
          <div
            id="product-preview-stage"
            role="group"
            tabIndex={0}
            aria-label={`${activeOption.title}, capability ${activeIndex + 1} of ${PREVIEW_OPTIONS.length}. Use the capability tabs or arrow keys to change capabilities.`}
            onKeyDown={handleKeyDown}
            onWheel={activeOption.number === '04' ? undefined : handleWheel}
            onPointerMove={
              activeOption.number === '04' ? undefined : handlePointerMove
            }
            onPointerLeave={resetStageTilt}
            className="relative min-w-0 overflow-hidden outline-none [perspective:1400px] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500"
          >
            {activeOption.number !== '04' && (
              <PreviewAtmosphere
                activeIndex={activeIndex}
                reduced={reduced}
              />
            )}
            <motion.div
              layout="size"
              drag={reduced || activeOption.number === '04' ? false : 'x'}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.075}
              dragMomentum={false}
              onDragEnd={handleDragEnd}
              style={{
                rotateX: activeOption.number === '04' ? 0 : stageRotateX,
                rotateY: activeOption.number === '04' ? 0 : stageRotateY,
                transformStyle: 'preserve-3d'
              }}
              transition={{
                layout: reduced
                  ? { duration: 0.01 }
                  : {
                      type: 'spring',
                      stiffness: 140,
                      damping: 24,
                      mass: 0.9
                    }
              }}
              className={`relative z-10 touch-pan-y ${activeOption.number === '04' ? '' : 'cursor-grab active:cursor-grabbing'}`}
            >
              <AnimatePresence
                initial={false}
                mode="wait"
              >
                <motion.div
                  key={activeOption.number}
                  initial={{
                    opacity: 0,
                    x: reduced ? 0 : direction * 68,
                    y: reduced ? 0 : 5,
                    rotateY: reduced ? 0 : direction * 4.25,
                    scale: reduced ? 1 : 0.978,
                    filter: reduced ? 'blur(0px)' : 'blur(4px)'
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    y: 0,
                    rotateY: 0,
                    scale: 1,
                    filter: 'blur(0px)'
                  }}
                  exit={{
                    opacity: 0,
                    x: reduced ? 0 : direction * -54,
                    y: reduced ? 0 : -3,
                    rotateY: reduced ? 0 : direction * -3.5,
                    scale: reduced ? 1 : 0.982,
                    filter: reduced ? 'blur(0px)' : 'blur(3px)'
                  }}
                  transition={
                    reduced
                      ? { duration: 0.01 }
                      : {
                          type: 'spring',
                          stiffness: 180,
                          damping: 28,
                          mass: 0.88
                        }
                  }
                  className="min-h-[540px] p-5 sm:p-7 md:min-h-[588px] md:p-9 lg:p-10"
                  aria-live="polite"
                >
                  {activeOption.number === '01' ? (
                    <ProspectingPreview />
                  ) : activeOption.number === '04' ? (
                    <HomepageMonitoringPreview reduced={reduced} />
                  ) : (
                    <IntelligenceExplorerPreview
                      previewNumber={activeOption.number}
                      reduced={reduced}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>

          <div className="flex justify-end border-t border-[color:var(--hairline-2)] px-5 py-1.5 sm:px-7 md:px-9">
            <Link
              href={capabilityHref(activeOption)}
              className="inline-flex min-h-11 items-center gap-2 rounded-sm text-xs font-medium text-[color:var(--accent-darker)] hover:underline hover:underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
            >
              Explore {activeOption.title.toLowerCase()}
              <ArrowUpRight
                className="size-4 shrink-0"
                aria-hidden="true"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function PreviewAtmosphere({
  activeIndex,
  reduced
}: {
  activeIndex: number;
  reduced: boolean;
}): React.JSX.Element {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      <motion.div
        key={`light-${activeIndex}`}
        initial={reduced ? false : { opacity: 0, x: -38, scale: 0.86 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{
          duration: reduced ? 0.01 : 0.9,
          ease: [0.2, 0.72, 0.24, 1]
        }}
        className="absolute -right-20 -top-36 h-[380px] w-[380px] rounded-full bg-teal-300/[0.08] blur-[90px]"
      />
      <motion.div
        animate={
          reduced
            ? undefined
            : {
                x: [0, 18, 0],
                y: [0, -9, 0],
                opacity: [0.22, 0.34, 0.22]
              }
        }
        transition={{
          duration: 9,
          ease: 'easeInOut',
          repeat: Infinity
        }}
        className="absolute bottom-20 left-[12%] h-16 w-16 rounded-full border border-teal-500/10"
      />
    </div>
  );
}

function ProspectingPreview(): React.JSX.Element {
  return (
    <>
      <header className="min-h-[108px] border-b border-[color:var(--hairline-2)] pb-6 md:min-h-0">
        <p className="font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-[color:var(--ink-4)]">
          Prospecting intelligence
        </p>
        <h2 className="display-h2 mt-3 text-[22px] leading-[1.08] text-[color:var(--ink)] md:text-[24px] lg:text-[26px]">
          Find people worth reaching now and see why.
        </h2>
      </header>

      <div className="pt-7 md:pt-9">
        <ExampleShortlist
          mode="Prospecting"
          showPersonas={false}
          embedded
          cycleModeExamples
        />
      </div>
    </>
  );
}
